import { V2_ENDPOINT } from "@/consts";
import { useCallback, useEffect, useState } from "react";

const STATS_API_BASE = V2_ENDPOINT;

interface NodeStatistics {
  nodeType: "trigger" | "action";
  nodeId: string;
  workflowsCount: number;
  totalExecutions: number;
  successRate: number | null;
  averageLatencyMs: number | null;
}

interface AllStatisticsResponse {
  code: number;
  message: string;
  data: {
    cronjob: NodeStatistics;
    "math-operation": NodeStatistics;
    "random-number": NodeStatistics;
    storage: NodeStatistics;
    // Add more as needed
    [key: string]: NodeStatistics;
  };
}

interface UseAllStatisticsResult {
  statistics: Record<string, NodeStatistics> | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch all workflow statistics
 */
export function useAllStatistics(): UseAllStatisticsResult {
  const [statistics, setStatistics] = useState<Record<string, NodeStatistics> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${STATS_API_BASE}/v1/statistics`);
      const data: AllStatisticsResponse = await response.json();

      if (data.code === 0 && data.data) {
        setStatistics(data.data);
      } else {
        setError(data.message || "Failed to fetch statistics");
      }
    } catch (err) {
      console.error("[useAllStatistics] Error:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch statistics");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    statistics,
    isLoading,
    error,
    refetch: fetchStatistics,
  };
}

interface TimeSeriesDataPoint {
  bucketStart: string;
  usageCount?: number;
  avgLatencyMs?: number;
  p50LatencyMs?: number;
  p95LatencyMs?: number;
}

interface TimeSeriesResponse {
  code: number;
  message: string;
  data: {
    nodeType: "trigger" | "action";
    nodeId: string;
    granularity: string;
    data: TimeSeriesDataPoint[];
  };
}

interface UseTimeSeriesOptions {
  nodeType: "trigger" | "action";
  nodeId: string;
  metricType: "usage" | "latency";
  from?: string;
  to?: string;
  granularity?: "hourly" | "daily";
  enabled?: boolean;
}

interface UseTimeSeriesResult {
  data: TimeSeriesDataPoint[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Format date to the required format: 2025-12-17T00:00:00.000000Z
 */
function formatDateForAPI(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const isoString = d.toISOString();
  // Convert from 2025-12-17T00:00:00.000Z to 2025-12-17T00:00:00.000000Z
  return isoString.replace(/\.(\d{3})Z$/, ".$1000Z");
}

/**
 * Hook to fetch time-series statistics (usage or latency)
 */
export function useTimeSeries({
  nodeType,
  nodeId,
  metricType,
  from,
  to,
  granularity = "hourly",
  enabled = true,
}: UseTimeSeriesOptions): UseTimeSeriesResult {
  const [data, setData] = useState<TimeSeriesDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeSeries = useCallback(async () => {
    if (!enabled) return;

    try {
      setIsLoading(true);
      setError(null);

      // Build query params with properly formatted dates
      const params = new URLSearchParams();
      if (from) params.append("from", formatDateForAPI(from));
      if (to) params.append("to", formatDateForAPI(to));
      if (granularity) params.append("granularity", granularity);

      const url = `${STATS_API_BASE}/v1/statistics/${nodeType}/${nodeId}/${metricType}?${params.toString()}`;
      const response = await fetch(url);
      const result: TimeSeriesResponse = await response.json();

      if (result.code === 0 && result.data) {
        setData(result.data.data || []);
      } else {
        setError(result.message || "Failed to fetch time series data");
      }
    } catch (err) {
      console.error("[useTimeSeries] Error:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch time series data");
    } finally {
      setIsLoading(false);
    }
  }, [nodeType, nodeId, metricType, from, to, granularity, enabled]);

  useEffect(() => {
    fetchTimeSeries();
  }, [fetchTimeSeries]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchTimeSeries,
  };
}

interface UseNodeStatisticsResult extends NodeStatistics {
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to get statistics for a specific node
 */
export function useNodeStatistics(nodeType: "trigger" | "action", nodeId: string): UseNodeStatisticsResult {
  const { statistics, isLoading, error } = useAllStatistics();

  const nodeStats = statistics?.[nodeId];

  return {
    nodeType: nodeStats?.nodeType || nodeType,
    nodeId: nodeStats?.nodeId || nodeId,
    workflowsCount: nodeStats?.workflowsCount || 0,
    totalExecutions: nodeStats?.totalExecutions || 0,
    successRate: nodeStats?.successRate || null,
    averageLatencyMs: nodeStats?.averageLatencyMs || null,
    isLoading,
    error,
  };
}
